import { useEffect, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "./App.css";
import "react-toastify/dist/ReactToastify.css";
import { AppRoutes } from "./routes";
import {
  API_URL,
  IS_AUTH_REQUIRED,
  IS_SIDEBAR_ENABLED,
  ALLOWED_APP,
} from "./config";
import storage, { AUTH_STATUS_EVENT } from "./utils/storage";
import Sidebar from "./layout/sidebar";
import ForbiddenPage from "./pages/public/permissionDenied";

function App() {
  const [isAuthReady, setIsAuthReady] = useState(!IS_AUTH_REQUIRED);
  const [isAuthenticated, setIsAuthenticated] = useState(
    storage.getAuthStatus()
  );
  const [isForbidden, setIsForbidden] = useState(false);

  useEffect(() => {
    if (IS_AUTH_REQUIRED) {
      setIsForbidden(false);
      return;
    }

    const allowedReferrers = ALLOWED_APP
      ? ALLOWED_APP.split(",").map((item) => item.trim()).filter(Boolean)
      : [];

    let forbidden = false;

    if (window.top === window.self) {
      forbidden = true;
    }

    const referrer = document.referrer;
    if (
      !referrer ||
      !allowedReferrers.some((allowedReferrer) =>
        referrer.startsWith(allowedReferrer)
      )
    ) {
      forbidden = true;
    }

    setIsForbidden(forbidden);
  }, []);

  useEffect(() => {
    const handleStatusChange = () => {
      setIsAuthenticated(storage.getAuthStatus());
    };
    window.addEventListener(AUTH_STATUS_EVENT, handleStatusChange);
    return () => {
      window.removeEventListener(AUTH_STATUS_EVENT, handleStatusChange);
    };
  }, []);

  useEffect(() => {
    if (!IS_AUTH_REQUIRED) {
      return;
    }

    const baseUrl = API_URL || "";

    const synchronizeSession = async () => {
      try {
        const response = await fetch(`${baseUrl}/auth/status/`, {
          method: "GET",
          credentials: "include",
        });

        if (!response.ok) {
          storage.clearAuthStatus();
          setIsAuthenticated(false);
          return;
        }

        const payload = await response.json();
        const active = Boolean(payload?.data?.is_authenticated);

        if (active) {
          storage.setAuthStatus(true);
          setIsAuthenticated(true);
        } else {
          storage.clearAuthStatus();
          setIsAuthenticated(false);
        }
      } catch (error) {
        storage.clearAuthStatus();
        setIsAuthenticated(false);
      } finally {
        setIsAuthReady(true);
      }
    };

    synchronizeSession();
  }, []);

  if (!isAuthReady) {
    return (
      <div className="login-page">
        <div className="login-card">
          <p style={{ margin: 0, textAlign: "center" }}>Checking access...</p>
        </div>
      </div>
    );
  }

  const shouldShowSidebar =
    IS_SIDEBAR_ENABLED && (!IS_AUTH_REQUIRED || isAuthenticated);

  if (!IS_AUTH_REQUIRED && isForbidden) {
    return <ForbiddenPage />;
  }

  return (
    <Router>
      <div className={`app-shell ${shouldShowSidebar ? "with-sidebar" : ""}`}>
        {shouldShowSidebar ? <Sidebar /> : null}
        <div className="app-shell__content">
          <ToastContainer />
          <AppRoutes
            isAuthenticated={isAuthenticated}
            isAuthRequired={IS_AUTH_REQUIRED}
          />
        </div>
      </div>
    </Router>
  );
}

export default App;
